// /use-cases — public counter-UAS use-cases page.
//
// Before this pass, the page used `.legal-doc` styling which bled its
// content across the full viewport with no hero and no cards. Rewritten
// in the `.section` / `.section__inner` / `.cap-card` marketing system
// so it is visually consistent with `/features`, `/evidence`, and the
// new `/docs/*` pages. Copy preserved (with small tightening).

import { Link } from "react-router-dom";
import {
    BookOpenCheck,
    ClipboardCheck,
    LineChart,
    Microscope,
    ShieldCheck,
    Target,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import { buildBreadcrumbListSchema } from "@/lib/schema";

const USE_CASES = [
    {
        eyebrow: "// DEFENCE PROCUREMENT",
        title: "Trade studies a scrutiny panel cannot dismantle.",
        icon: <ClipboardCheck size={22} />,
        who: "Programme offices, capability-branch decision teams, and technical reviewers preparing an options analysis for a counter-UAS investment.",
        what:
            "Build a trade-study scenario from validated presets. Sweep Monte Carlo across the threat classes you expect. Export a procurement-ready evidence pack. Every input carries its source. Every model touched carries its validity envelope. Every metric carries a regression comparison to the last blessed baseline.",
        walkout: "A defensible options analysis and a transparent audit trail of every input that fed it.",
        link: { to: "/evidence", label: "the credibility framework", testId: "use-cases-link-evidence" },
    },
    {
        eyebrow: "// INTERCEPTOR CALIBRATION",
        title: "Make the model match what the airframe actually did.",
        icon: <LineChart size={22} />,
        who: "Systems-engineering teams with real telemetry (PX4 ULog, ArduPilot binary, MAVLink tlog, or CSV) who need to tune model parameters against field data.",
        what:
            "Import the log (PX4 ULog, ArduPilot binary, MAVLink tlog, or CSV). Propose parameter deltas. Replay the engagement. Read the before/after side by side. Accepted deltas bump the model version. The audit trail records who signed off, on what evidence, and when.",
        walkout: "An updated, version-bumped model in the registry — and a calibration session document any reviewer can re-open later.",
        link: { to: "/features", label: "the feature tour", testId: "use-cases-link-features" },
    },
    {
        eyebrow: "// OPERATOR REHEARSAL",
        title: "See the tails of the distribution before the range.",
        icon: <Target size={22} />,
        who: "Operator cells preparing for a live-fire exercise or a contested-environment deployment.",
        what:
            "Build mission scenarios that mirror the engagement envelope. Run thousands of Monte Carlo variants. Review the failure modes in a structured decision log — on the same console the analysts use. No separate training rig. No drift between training and production data.",
        walkout: "A mission plan you have already seen survive the tails of the distribution, not just the nominal case.",
        link: { to: "/demo", label: "verified intercept demonstrations", testId: "use-cases-link-demo" },
    },
    {
        eyebrow: "// RESEARCH + CAPABILITY DEVELOPMENT",
        title: "A test bench someone else can re-run six months from now.",
        icon: <Microscope size={22} />,
        who: "Defence-science teams and technology partners needing a reproducible counter-UAS test bench.",
        what:
            "Register new interceptor or threat models. Publish their validity envelopes. Run the regression harness against the golden baseline. Ship the evidence as signed PDF and DOCX. Every run captures an immutable parameter-provenance snapshot a third party can re-validate independently.",
        walkout: "A scientifically repeatable test record — not a folder of screenshots.",
        link: { to: "/docs/evidence", label: "how the provenance chain works", testId: "use-cases-link-docs-evidence" },
    },
];

export default function UseCasesPage() {
    return (
        <>
            <SeoHead
                path="/use-cases"
                schemaJsonLd={[
                    buildBreadcrumbListSchema({
                        path: "/use-cases",
                        label: "Use cases",
                    }),
                ]}
            />
            <div data-testid="use-cases-page">
                <section className="section" style={{ paddingTop: 64 }}>
                    <div className="section__inner">
                        <span className="section__eyebrow">// WHO USES IT</span>
                        <h1 className="section__heading" data-testid="use-cases-h1">
                            Built for the people whose decisions defend.
                        </h1>
                        <p className="section__lede">
                            Four teams. Four pressures. One platform that survives all
                            of them — the programme office picking the counter-UAS
                            interceptor, the engineering team calibrating it, the
                            operators rehearsing with it, and the researchers
                            validating new capability against the golden baseline.
                            Here is how each one uses UAVSimNet and what they walk
                            out with.
                        </p>
                    </div>
                </section>

                {USE_CASES.map((u, idx) => (
                    <section
                        key={u.title}
                        className={"section" + (idx % 2 === 1 ? " section--alt" : "")}
                        data-testid={`use-case-${idx}`}
                    >
                        <div
                            className="section__inner"
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 2fr",
                                gap: 48,
                                alignItems: "start",
                            }}
                        >
                            <div className="scroll-reveal">
                                <div className="cap-card__icon">{u.icon}</div>
                                <span className="section__eyebrow">{u.eyebrow}</span>
                                <h2
                                    className="section__heading"
                                    style={{ fontSize: 26, lineHeight: 1.25 }}
                                >
                                    {u.title}
                                </h2>
                            </div>
                            <div
                                className="scroll-reveal"
                                data-delay="120"
                                style={{ display: "grid", gap: 18 }}
                            >
                                <div>
                                    <div
                                        style={{
                                            fontFamily: "'JetBrains Mono', monospace",
                                            color: "var(--ux-accent)",
                                            fontSize: 11,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.12em",
                                            marginBottom: 4,
                                        }}
                                    >
                                        Who
                                    </div>
                                    <p className="section__lede" style={{ margin: 0 }}>
                                        {u.who}
                                    </p>
                                </div>
                                <div>
                                    <div
                                        style={{
                                            fontFamily: "'JetBrains Mono', monospace",
                                            color: "var(--ux-accent)",
                                            fontSize: 11,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.12em",
                                            marginBottom: 4,
                                        }}
                                    >
                                        What they do
                                    </div>
                                    <p className="section__lede" style={{ margin: 0 }}>
                                        {u.what}
                                    </p>
                                </div>
                                <div>
                                    <div
                                        style={{
                                            fontFamily: "'JetBrains Mono', monospace",
                                            color: "var(--ux-accent)",
                                            fontSize: 11,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.12em",
                                            marginBottom: 4,
                                        }}
                                    >
                                        Walk out with
                                    </div>
                                    <p className="section__lede" style={{ margin: 0 }}>
                                        {u.walkout}{" "}
                                        <Link
                                            to={u.link.to}
                                            data-testid={u.link.testId}
                                            style={{
                                                color: "var(--ux-accent)",
                                                textDecoration: "none",
                                                borderBottom: "1px dotted var(--ux-accent)",
                                            }}
                                        >
                                            Related — {u.link.label}
                                        </Link>
                                        .
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                ))}

                <section className="section">
                    <div className="section__inner" style={{ textAlign: "center" }}>
                        <span className="section__eyebrow">// NEXT</span>
                        <h2 className="section__heading">
                            Decide which one is yours.
                        </h2>
                        <p
                            className="section__lede"
                            style={{ margin: "0 auto 24px", maxWidth: 720 }}
                        >
                            Every use case above maps onto concrete platform
                            capabilities and a reviewer-signed evidence trail. Pick
                            a thread and pull it.
                        </p>
                        <div
                            style={{
                                display: "inline-flex",
                                gap: 12,
                                flexWrap: "wrap",
                                justifyContent: "center",
                            }}
                        >
                            <Link
                                to="/features"
                                className="btn-primary"
                                data-testid="use-cases-cta-features"
                            >
                                Capability tour
                            </Link>
                            <Link
                                to="/evidence"
                                className="btn-ghost"
                                data-testid="use-cases-cta-evidence"
                            >
                                <ShieldCheck size={14} style={{ marginRight: 6 }} />
                                Credibility framework
                            </Link>
                            <Link
                                to="/docs/use-cases"
                                className="btn-ghost"
                                data-testid="use-cases-cta-docs"
                            >
                                <BookOpenCheck size={14} style={{ marginRight: 6 }} />
                                Docs · use cases
                            </Link>
                            <Link
                                to="/contact"
                                className="btn-ghost"
                                data-testid="use-cases-cta-contact"
                            >
                                Request access
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}
